import { sortBy } from "lodash-es";

import Email from "@mapmycustomers/shared/types/email/Email";
import EmailRecipientAddress from "@mapmycustomers/shared/types/email/EmailRecipientAddress";
import { Activity } from "@mapmycustomers/shared/types/entity";

import isEmailDeliveryFailed from "./isEmailDeliveryFailed";
import normalizeEmail from "./normalizeEmail";

export interface SentEmailRecipient extends EmailRecipientAddress {
  // The log this recipient was addressed on. Each recipient of a merged multi-contact
  // send has its own log, and the entity behind an address is looked up by log id.
  emailId: Email["id"];
}

// Lists the recipients of a sent email activity. A merged multi-contact send is one
// activity carrying one log per recipient, so the singular `emailLog` — the anchor row —
// names only one person; the plural `emailLogs` (detail read only) names the whole send.
const getSentEmailRecipients = (activity: Activity): SentEmailRecipient[] => {
  // The server returns logs unordered and only the first few show, so keep the anchor's
  // recipient first and the rest in log-id order.
  const anchorLogId = activity.emailLog?.id;
  const allLogs = activity.emailLogs?.length
    ? sortBy(activity.emailLogs, [(log) => log.id !== anchorLogId, (log) => log.id])
    : activity.emailLog
      ? [activity.emailLog]
      : [];
  // A recipient who never received it does not belong on To:, and for a bounce or a cancel
  // the processor has already dropped them from the activity's contacts. Filtering per log
  // matches its per-contact rule: emailLogs is unique on (emailId, entityId) and an
  // activity's logs are all one send, so a contact has exactly one log here.
  const logs = allLogs.filter((log) => !isEmailDeliveryFailed(log));

  // An address can repeat across logs (cc'd on another's, or a reply to several); list it
  // once in first-seen order, and let a later copy that has a name replace a nameless one.
  const recipientsByAddress = new Map<string | undefined, SentEmailRecipient>();
  logs.forEach((log) =>
    (log.recipientAddress ?? []).forEach((recipient) => {
      const address = normalizeEmail(recipient.email);
      const seenRecipient = recipientsByAddress.get(address);
      if (!seenRecipient || (!seenRecipient.name && recipient.name)) {
        recipientsByAddress.set(address, { ...recipient, emailId: log.id });
      }
    })
  );
  return Array.from(recipientsByAddress.values());
};

export default getSentEmailRecipients;
